import { NoiseEventPolicyOverrideEnum } from 'app/__generated__/global';
import { KnownAccessControls } from 'app/business-logic/security/KnownAccessControls';
import { NoiseEvent } from 'app/business-logic/services/noise-service/noiseService.types';
import { useHasAccess } from 'app/hooks/use-has-access';
import { DATA_TRACKING_KEY } from 'app/views/components/gainsight/trackingKeys';
import { useResourceGroups } from 'app/views/configuration/pages/permission/resource-groups/hooks/useResourceGroups';
import { useI18n } from 'core/hooks/useI18n';
import { Form, Formik, useFormikContext } from 'formik';
import { useEventClassificationInitialState } from '../../hooks/use-event-classification-initial-state';
import { EventAccess } from '../event-access/EventAccess';
import { EventAccessFormValues } from '../event-access/EventAccess.types';
import { useUpdateNoiseEventPolicyOverride } from '../event-access/useUpdateNoiseEventPolicyOverride';
import { EventClassificationButtons } from '../event-classification/event-classification-buttons/EventClassificationButtons';
import { EventClassificationComments } from '../event-classification/event-classification-comments/EventClassificationComments';
import { EventClassificationState } from '../event-classification/EventClassification.types';
import { useClassifyNoiseEvent } from '../event-classification/useClassifyNoiseEvent';
import { ButtonsWrapper, ConfirmButton, DiscardButton } from './EventHandling.styles';

type EventHandlingProps = { event: NoiseEvent; handleExpansion: () => void };

type EventHandlingFormValues = EventClassificationState & EventAccessFormValues;

export const EventHandling = (props: EventHandlingProps) => {
  const { event, handleExpansion } = props;
  const { initialState, isFetching: initialStateIsLoading } = useEventClassificationInitialState({ event });
  const { data: resourceGroups } = useResourceGroups();
  const { classifyNoiseEvent, isPending: isSubmitting } = useClassifyNoiseEvent({
    event,
    onSuccess: () => handleExpansion(),
  });
  const { mutateAsync: updateNoiseEventPolicyOverride, isPending: isUpdating } = useUpdateNoiseEventPolicyOverride();
  const noiseEventPolicyOverride = resourceGroups.map(rg => {
    return {
      resourceGroupId: rg.id,
      resourceGroupName: rg.name,
      defaultNoiseEventPolicy: rg.noiseEventPolicy || null,
      override:
        event.noiseEventPolicyOverride?.find(override => override.resourceGroupId === rg.id)?.override ||
        NoiseEventPolicyOverrideEnum.Default,
    };
  });

  const isLoading = initialStateIsLoading || isSubmitting || isUpdating;

  const formInitialValues: EventHandlingFormValues = { ...initialState, noiseEventPolicyOverride };

  const hasClassificationChanged = (initial: EventHandlingFormValues, current: EventHandlingFormValues) => {
    if (initial.disregarded !== current.disregarded) return true;
    if (initial.comments !== current.comments) return true;
    if (initial.classifications.length !== current.classifications.length) return true;
    return initial.classifications.some((c, i) => c.isSelected !== current.classifications[i]?.isSelected);
  };

  const getChangedAccessOverrides = (initial: EventHandlingFormValues, current: EventHandlingFormValues) => {
    return current.noiseEventPolicyOverride.filter((item, index) => {
      const initialItem = initial.noiseEventPolicyOverride[index];
      return initialItem?.override !== item.override;
    });
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={formInitialValues}
      onSubmit={values => {
        if (hasClassificationChanged(formInitialValues, values)) {
          classifyNoiseEvent({
            classifications: values.classifications,
            comments: values.comments,
            disregarded: values.disregarded,
          });
        }
        const changedAccessOverrides = getChangedAccessOverrides(formInitialValues, values);
        changedAccessOverrides.forEach(({ resourceGroupId, override }) => {
          updateNoiseEventPolicyOverride({
            noiseEventId: event.id,
            resourceGroupId,
            override: override ?? NoiseEventPolicyOverrideEnum.Default,
          });
        });
      }}
    >
      <Form>
        <EventHandlingForm isLoading={isLoading} {...props} />
      </Form>
    </Formik>
  );
};

const EventHandlingForm = (props: EventHandlingProps & { isLoading: boolean }) => {
  const hasAccess = useHasAccess();
  const { l10n } = useI18n('app/views/alerts', 'eventClassification');
  const { dirty, resetForm } = useFormikContext<EventHandlingFormValues>();
  const { event, handleExpansion, isLoading } = props;

  const canEdit = hasAccess(
    [KnownAccessControls.app.noise.classification.data.write._, KnownAccessControls.app.config.resourceGroups._],
    'some'
  );

  return (
    <>
      <EventClassificationButtons event={event} isLoading={isLoading} />
      <EventAccess isLoading={isLoading} />
      <EventClassificationComments event={event} isLoading={isLoading} />
      {canEdit && (
        <ButtonsWrapper>
          <ConfirmButton
            $variant="outlined"
            type="submit"
            disabled={!dirty || isLoading}
            data-tracking={DATA_TRACKING_KEY['event-history-save-button']}
          >
            {l10n('actions.confirm')}
          </ConfirmButton>
          <DiscardButton
            $variant="text"
            onClick={e => {
              e.preventDefault();
              resetForm();
              handleExpansion();
            }}
            data-tracking={DATA_TRACKING_KEY['event-history-discard-button']}
          >
            {l10n('actions.discard')}
          </DiscardButton>
        </ButtonsWrapper>
      )}
    </>
  );
};
